import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import CalendarioFeriados from '../components/CalendarioFeriados';
import ConfigBeneficioModal from '../components/ConfigBeneficioModal';
import DiferencaMesAnterior from '../components/DiferencaMesAnterior';
import TravaBeneficio from '../components/TravaBeneficio';
import '../styles/bporh_admissoes.css';

// pessoas do mês para o "por que deu diferença": a mesma pessoa pode ter várias
// linhas (ida/volta, operadoras) — o componente soma por convenia_id
const pessoasVt = (d) => (d?.operadoras || []).flatMap(o => (o.colaboradores || []).map(c => ({
  id: c.convenia_id, nome: c.nome, valor: c.total || 0, dias: c.dias,
  admissao: c.admissao, desligado: c.desligado,
  motivo: (c.alertas || []).map(a => (typeof a === 'string' ? a : a?.texto)).filter(Boolean)[0] || null,
})));

// Controle de Vale Transporte — a "planilha viva" do VT, no mesmo formato que a
// operação já usa: um bloco por OPERADORA (a Inova tem TEU e TRI), com nome,
// passagens por dia, valor unitário, valor por dia, dias e total.
//
// Diferente do VA, o VT NÃO vem da Convenia: quem tem, em qual operadora e a que
// preço só existe aqui. Por isso o colaborador precisa ser incluído à mão — e
// quem ainda não foi aparece na caixa "sem VT" ao lado, que é como uma admissão
// nova chega para quem toca o VT.

const brl = (v) => (v == null ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

const num = (v) => (v == null || v === '' ? ''
  : String(v).replace('.', ','));

// A recarga é comprada no dia 25 e vale para o mês SEGUINTE: o mês da tela é o
// da COMPRA (em setembro compra-se o vale de outubro).
const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// a recarga comprada no dia 25 do mês escolhido é usada no mês seguinte; o
// desconto sai na folha do mês da compra (mesma regra do selo da tela)
const mesDepois = (m) => {
  const [ano, mm] = (m || '').split('-').map(Number);
  if (!ano || !mm) return m || '';
  const d = new Date(ano, mm, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const mesExtenso = (m) => {
  const [ano, mm] = (m || '').split('-');
  if (!ano || !mm) return m || '';
  const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${nomes[Number(mm) - 1] || mm}/${ano}`;
};

// '12345678901' -> '123.456.789-01' (como na planilha de controle do cliente)
const fmtCpf = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (v || '');
};
const mesPorExtenso = (m) => {
  const t = mesExtenso(m);
  const [nome, ano] = t.split('/');
  return ano ? `${nome.charAt(0).toUpperCase()}${nome.slice(1)} de ${ano}` : t;
};

const COR_ALERTA = {
  ferias: { bg: '#e3f2fd', cor: '#0a6ea1', icone: 'fa-umbrella-beach' },
  afastamento: { bg: '#f3e8fb', cor: '#7a3b9e', icone: 'fa-notes-medical' },
  novo: { bg: '#e6f7ef', cor: '#116b47', icone: 'fa-user-plus' },
  desligado: { bg: '#fdeaec', cor: '#a11d2b', icone: 'fa-user-xmark' },
  experiencia: { bg: '#fff8e6', cor: '#8a6d00', icone: 'fa-hourglass-half' },
  experiencia_vence: { bg: '#ffe8cc', cor: '#b5560e', icone: 'fa-triangle-exclamation' },
};

// de onde saiu o número de dias daquela linha
const ORIGEM_DIAS = {
  calculado: { rotulo: 'dias úteis do mês, menos férias/afastamento e proporcional à admissão ou desligamento', cor: '#6c757d' },
  fixo: { rotulo: 'número fixo de dias deste colaborador (não vem todo dia)', cor: '#0a6ea1' },
  manual: { rotulo: 'ajustado à mão nesta competência', cor: '#b5560e' },
};

const BpoRhValeTransporte = () => {
  // dois caminhos: menu "Vale Transporte" (escolhe a empresa) ou link direto
  const { clienteId: clienteIdRota } = useParams();
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // empresa e mês vivem na URL: o F5 mantém a tela aberta e o link é compartilhável
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteSel = searchParams.get('cliente') || '';
  const clienteId = clienteIdRota || clienteSel;
  const mes = searchParams.get('mes') || mesAtual();
  const setParam = (chave, valor) => setSearchParams(p => {
    const n = new URLSearchParams(p);
    if (valor) n.set(chave, valor); else n.delete(chave);
    return n;
  });

  // ─── entrada: portais de VT de todas as empresas ───
  const [portais, setPortais] = useState([]);
  const [carregandoPortais, setCarregandoPortais] = useState(false);
  useEffect(() => {
    if (clienteId) return;
    setCarregandoPortais(true);
    axios.get(`${API}/api/bpo-vt/portais/`, { headers: headers() })
      .then(res => setPortais(res.data.servicos || []))
      .catch(() => setMensagem('❌ Não foi possível listar os portais de VT'))
      .finally(() => setCarregandoPortais(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // credenciais do portal: a listagem só diz se existe senha; revelar é uma
  // chamada própria e fica registrada (mesma regra das outras telas)
  const [senhasPortal, setSenhasPortal] = useState({});
  const copiar = (texto, rotulo) => {
    navigator.clipboard?.writeText(texto)
      .then(() => setMensagem(`✅ ${rotulo} copiado`))
      .catch(() => setMensagem('❌ Não foi possível copiar'));
  };
  const revelarSenha = (servicoId) => {
    if (senhasPortal[servicoId] != null) {
      copiar(senhasPortal[servicoId], 'Senha');
      return;
    }
    axios.get(`${API}/api/bporh-credencial/`,
      { params: { tipo: 'servico', id: servicoId, campo: 'senha' }, headers: headers() })
      .then((res) => {
        setSenhasPortal(s => ({ ...s, [servicoId]: res.data.valor || '' }));
        copiar(res.data.valor || '', 'Senha');
      })
      .catch(() => setMensagem('❌ Não foi possível obter a senha'));
  };
  useEffect(() => { setSenhasPortal({}); }, [clienteId]);

  // ─── o mês da empresa ───
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);

  // silencioso = atualiza os números SEM desmontar a tela: cada edição inline
  // recarregava o mês com spinner e parecia um refresh da página inteira
  const carregar = useCallback((silencioso = false) => {
    if (!clienteId) { setDados(null); return; }
    if (!silencioso) setCarregando(true);
    axios.get(`${API}/api/bpo-vt/${clienteId}/`, { params: { mes }, headers: headers() })
      .then(res => setDados(res.data))
      .catch(err => {
        setDados(null);
        setMensagem(`❌ ${err.response?.data?.erro || 'Não foi possível carregar o vale transporte'}`);
      })
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, mes]);
  useEffect(() => { carregar(); }, [carregar]);
  // e-mail enviado = mês travado: a tela mostra a foto e o que é do mês fica bloqueado
  const travado = !!dados?.trava?.travado;

  // calendário de feriados (mesmo editor do Controle de VA)
  const [feriadosAberto, setFeriadosAberto] = useState(false);
  const [configAberto, setConfigAberto] = useState(false);   // botão Configurar (dias + valores)

  // ─── operadoras ───
  const [novaOperadora, setNovaOperadora] = useState('');
  const [salvandoOper, setSalvandoOper] = useState(false);
  const criarOperadora = () => {
    const nome = novaOperadora.trim();
    if (!nome) return;
    setSalvandoOper(true);
    axios.post(`${API}/api/bpo-vt/${clienteId}/operadora/`, { acao: 'criar', nome },
      { headers: headers() })
      .then(() => { setNovaOperadora(''); setMensagem(`✅ Operadora ${nome} criada`); carregar(true); })
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível criar'}`))
      .finally(() => setSalvandoOper(false));
  };
  const [operadoraExcluir, setOperadoraExcluir] = useState(null);
  const excluirOperadora = (oper) => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/operadora/`, { acao: 'excluir', id: oper.id },
      { headers: headers() })
      .then(() => { setMensagem(`✅ ${oper.nome} excluída`); setOperadoraExcluir(null); carregar(true); })
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível excluir'}`));
  };

  // ─── cadastro/edição do colaborador na operadora ───
  const salvarValor = (corpo, aviso) => (
    axios.post(`${API}/api/bpo-vt/${clienteId}/valor/`, corpo, { headers: headers() })
      .then(() => { if (aviso) setMensagem(aviso); carregar(true); })
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`))
  );

  // todo salvamento reenvia o cadastro inteiro — o backend regrava a linha,
  // então mandar só o campo alterado apagaria os outros
  const baseValor = (c, op, mudanca) => ({
    id: c.valor_id,
    convenia_id: c.convenia_id, operadora_id: op.id, linha: c.linha || '',
    modalidade: c.modalidade || 'passagem',
    quantidade: c.quantidade, valor_unitario: c.valor_unitario,
    dias_fixos: c.dias_fixos, obs: c.obs,
    departamento: c.departamento_ov || '', centro_custo: c.centro_custo_ov || '',
    ...mudanca,
  });

  // DIAS À DESCONTAR daquela linha na competência, com o motivo — é como a
  // planilha do cliente trabalha ('desconto de 16 dias referente ao reembolso')
  const salvarAjuste = (c, op, mudanca) => (
    axios.post(`${API}/api/bpo-vt/${clienteId}/ajuste/`, {
      id: c.valor_id, mes, descontar: c.descontar, obs: c.desconto_obs, ...mudanca,
    }, { headers: headers() })
      .then(() => carregar(true))
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o ajuste'}`))
  );

  // desconto/repasse/serviço do boleto da unidade
  const salvarFechamento = (op, mudanca) => (
    axios.post(`${API}/api/bpo-vt/${clienteId}/fechamento/`, {
      operadora_id: op.id, mes, ...op.fechamento, ...mudanca,
    }, { headers: headers() })
      .then(() => carregar(true))
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o fechamento'}`))
  );

  // dados da unidade (empresa e CNPJ ficam no cabeçalho do bloco)
  const [editarUnidade, setEditarUnidade] = useState(null);
  const salvarUnidade = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/operadora/`,
      { acao: 'editar', id: editarUnidade.id, nome: editarUnidade.nome,
        empresa: editarUnidade.empresa, cnpj: editarUnidade.cnpj },
      { headers: headers() })
      .then(() => { setEditarUnidade(null); setMensagem('✅ Unidade salva'); carregar(true); })
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`));
  };

  // inclusão: escolhe quem, em qual operadora, e o preço da passagem
  const [incluir, setIncluir] = useState(null);   // {operadora_id, operadora}
  const FORM_INCLUIR_VAZIO = { convenia_id: '', linha: '', modalidade: 'passagem',
    quantidade: '2', valor_unitario: '',
    dias_fixos: '', departamento: '', centro_custo: '', depto_origem: '', cc_origem: '' };
  const [formIncluir, setFormIncluir] = useState(FORM_INCLUIR_VAZIO);
  const abrirIncluir = (oper) => {
    setIncluir(oper);
    setFormIncluir(FORM_INCLUIR_VAZIO);
  };
  // mais uma linha para quem já tem VT (3 ônibus = 3 linhas): abre a inclusão já
  // com a pessoa escolhida e a mesma quantidade/valor da linha de onde partiu
  const abrirOutraLinha = (oper, c) => {
    setIncluir(oper);
    setFormIncluir({
      ...FORM_INCLUIR_VAZIO, convenia_id: c.convenia_id,
      modalidade: c.modalidade || 'passagem',
      quantidade: c.modalidade === 'fixo' ? '1' : String(c.quantidade ?? 2).replace('.', ','),
      valor_unitario: c.valor_unitario != null ? String(c.valor_unitario).replace('.', ',') : '',
      departamento: c.departamento || '', centro_custo: c.centro_custo || '',
      depto_origem: c.departamento || '', cc_origem: c.centro_custo || '',
    });
  };
  // escolher a pessoa no modal já traz o departamento e o centro de custo dela:
  // da Convenia, ou o rótulo do cliente se ela já estiver em outra operadora
  const escolherColaborador = (convenia_id) => {
    const p = semVt.find(x => x.convenia_id === convenia_id)
      || todasLinhas.map(({ c }) => c).find(c => c.convenia_id === convenia_id);
    setFormIncluir(f => ({
      ...f, convenia_id,
      departamento: (p?.departamento) || '',
      centro_custo: (p?.centro_custo) || '',
      // guarda o valor de origem: se o operador não mexer, não vira override —
      // assim uma correção futura na Convenia continua chegando na tela
      depto_origem: (p?.departamento) || '',
      cc_origem: (p?.centro_custo) || '',
    }));
  };

  const confirmarIncluir = () => {
    if (!formIncluir.convenia_id) { setMensagem('❌ Escolha o colaborador'); return; }
    if (!formIncluir.valor_unitario) { setMensagem('❌ Informe o valor unitário da passagem'); return; }
    const { depto_origem, cc_origem, ...campos } = formIncluir;
    salvarValor({
      ...campos, operadora_id: incluir.id,
      departamento: campos.departamento === depto_origem ? '' : campos.departamento,
      centro_custo: campos.centro_custo === cc_origem ? '' : campos.centro_custo,
    }, `✅ Colaborador incluído em ${incluir.nome}`).then(() => setIncluir(null));
  };

  const [remover, setRemover] = useState(null);   // {colab, operadora}

  // ─── recarga paga ───
  const [salvandoPgto, setSalvandoPgto] = useState(false);
  const [confirmandoDesmarcar, setConfirmandoDesmarcar] = useState(false);
  const itemPgto = (c, oper) => ({
    id: c.valor_id,
    convenia_id: c.convenia_id, operadora_id: oper.id, linha: c.linha || '',
    nome: c.nome, cpf: c.cpf,
    operadora: oper.nome, quantidade: c.quantidade, valor_unitario: c.valor_unitario,
    dias: c.dias, valor: c.total,
  });
  const marcarPago = (itens, pago) => {
    if (travado) { setMensagem('🔒 Mês travado: reabra o mês para marcar ou desmarcar'); return; }
    if (!itens.length) return;
    setSalvandoPgto(true);
    axios.post(`${API}/api/bpo-vt/${clienteId}/pagar/`, { mes, pago, itens },
      { headers: headers() })
      .then(res => { setMensagem(`✅ ${res.data.mensagem}`); carregar(true); })
      .catch(err => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível registrar'}`))
      .finally(() => setSalvandoPgto(false));
  };

  // ─── Boleto da recarga: anexa o PDF, monta o e-mail com a relação dos
  // colaboradores e manda ao financeiro do cliente. Tudo fica no histórico. ───
  const [boletoAberto, setBoletoAberto] = useState(false);
  const [boletoDados, setBoletoDados] = useState(null);   // anexos + histórico + e-mails
  const [boletoPrevia, setBoletoPrevia] = useState(null);
  const [boletoForm, setBoletoForm] = useState({ emails: '', copia: '', vencimento: '',
    taxa_boleto: '' });
  const [boletoSel, setBoletoSel] = useState([]);         // ids anexados a enviar
  // quem assina o e-mail: o operador que atende o cliente (senha de app em
  // bporh_operador). Vem sugerido, mas dá para trocar.
  const [boletoOperador, setBoletoOperador] = useState('');
  const [subindoBoleto, setSubindoBoleto] = useState(false);
  const [enviandoBoleto, setEnviandoBoleto] = useState(false);
  // ─── planilha de Contas a Pagar da Omie: uma conta por unidade, só os marcados,
  //     com a taxa do boleto dividida igualmente entre os colaboradores ───
  const [omieVt, setOmieVt] = useState(null);   // {vencimento, taxa, categoria, fornecedores, departamentos, previa, carregando}
  const corpoOmieVt = (o, extra) => ({
    mes, vencimento: o.vencimento, taxa: o.taxa, categoria: o.categoria,
    fornecedores: o.fornecedores, departamentos: o.departamentos, ...extra,
  });
  const previaOmieVt = (o) => {
    setOmieVt((x) => ({ ...(x || o), carregando: true }));
    axios.post(`${API}/api/bpo-vt/${clienteId}/omie/`, corpoOmieVt(o), { headers: headers(), timeout: 180000 })
      .then((r) => setOmieVt((x) => ({
        ...(x || o), carregando: false, previa: r.data,
        // o servidor devolve o fornecedor salvo de cada unidade: preenche os campos vazios
        fornecedores: Object.fromEntries((r.data.unidades || []).map(u => [String(u.id),
          (x || o).fornecedores[String(u.id)] ?? u.fornecedor ?? ''])),
        taxa: (x || o).taxa === '' ? String(r.data.taxa ?? '').replace('.', ',') : (x || o).taxa,
      })))
      .catch((e) => { setOmieVt((x) => x && ({ ...x, carregando: false }));
        setMensagem(`❌ ${e.response?.data?.mensagem || 'Não foi possível montar a planilha'}`); });
  };
  const abrirOmieVt = () => {
    const o = { vencimento: boletoForm.vencimento || '', taxa: boletoForm.taxa_boleto || '',
      categoria: 'Vale Transporte', fornecedores: {}, departamentos: {}, previa: null };
    setOmieVt(o);
    previaOmieVt(o);
  };
  // arquivo de recarga da Swile para a MOBILIDADE (Mercatto/Setho): valor na coluna Mobilidade
  const temMobilidade = (dados?.operadoras || []).some(op => /mobilidade/i.test(op.nome || ''));
  const baixarSwile = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/swile/`, { mes },
      { headers: headers(), responseType: 'blob', timeout: 180000 })
      .then((r) => {
        const m = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = m ? m[1] : `swile_mobilidade_${mes}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        setMensagem(`✅ Arquivo da Swile gerado com ${r.headers['x-total-linhas'] || ''} colaborador(es)`
          + (r.headers['x-so-marcados'] === '1' ? ' (só os marcados)' : ' — ninguém marcado: foram todos com valor'));
      })
      .catch(async (e) => {
        let msg = 'Não foi possível gerar o arquivo';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém */ }
        setMensagem(`❌ ${msg}`);
      });
  };
  const mudarOmieVt = (campo, valor) => setOmieVt((x) => ({ ...x, [campo]: valor }));
  const baixarOmieVt = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/omie/`, corpoOmieVt(omieVt, { baixar: true }),
      { headers: headers(), responseType: 'blob', timeout: 180000 })
      .then((r) => {
        const m = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = m ? m[1] : `Omie_Contas_Pagar_VT_${mes}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(async (e) => {
        let msg = 'Não foi possível gerar a planilha';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém */ }
        setMensagem(`❌ ${msg}`);
      });
  };

  // planilha de controle (layout Rubeus) vai por e-mail em vez de ser baixada
  const [planilhaAberto, setPlanilhaAberto] = useState(false);
  const [planilhaPrevia, setPlanilhaPrevia] = useState(null);
  const [enviandoPlanilha, setEnviandoPlanilha] = useState(false);
  const boletoInput = React.useRef(null);

  const carregarBoleto = useCallback(() => {
    if (!clienteId) return Promise.resolve();
    return axios.get(`${API}/api/bpo-vt/${clienteId}/boleto/`,
      { params: { mes }, headers: headers() })
      .then((res) => {
        setBoletoDados(res.data);
        // O cadastro do cliente MANDA: preservar o que estava digitado fazia o
        // e-mail do financeiro do cliente anterior sobreviver à troca de empresa
        // (o seletor troca clienteId sem desmontar a tela) e o boleto ia para o
        // financeiro errado.
        setBoletoForm((f) => ({
          ...f,
          emails: res.data.financeiro_emails || '',
          copia: res.data.financeiro_copia || '',
        }));
        setBoletoSel((res.data.boletos || []).map(b => b.id));
        setBoletoOperador(String(res.data.operador_sugerido_id || ''));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, mes]);

  const abrirBoleto = () => {
    setBoletoAberto(true);
    setBoletoPrevia(null);
    // taxa do boleto: sugere a soma do 'Taxa de serviço' dos fechamentos das
    // unidades — o operador corrige se o boleto vier diferente
    const sugestao = (dados?.operadoras || [])
      .reduce((n, op) => n + (op.fechamento?.servico || 0), 0);
    setBoletoForm(f => ({
      ...f,
      taxa_boleto: f.taxa_boleto || (sugestao ? String(sugestao.toFixed(2)).replace('.', ',') : ''),
    }));
    carregarBoleto().catch(() => setMensagem('❌ Não foi possível carregar os boletos'));
  };

  const anexarBoleto = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('arquivo', file);
    fd.append('mes', mes);
    setSubindoBoleto(true);
    axios.post(`${API}/api/bpo-vt/${clienteId}/boleto/`, fd, { headers: headers() })
      .then((res) => {
        setBoletoDados((d) => ({ ...(d || {}), boletos: res.data.boletos || [] }));
        setBoletoSel((res.data.boletos || []).map(b => b.id));
        setMensagem(`✅ ${res.data.mensagem}`);
      })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível anexar o boleto'}`))
      .finally(() => setSubindoBoleto(false));
  };

  const removerBoleto = (b) => {
    axios.delete(`${API}/api/bpo-vt/${clienteId}/boleto/`,
      { params: { id: b.id }, headers: headers() })
      .then(() => carregarBoleto())
      .catch(() => setMensagem('❌ Não foi possível remover o boleto'));
  };

  const salvarFinanceiro = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/financeiro/`,
      { emails: boletoForm.emails, copia: boletoForm.copia }, { headers: headers() })
      .then(() => setMensagem('✅ E-mails do financeiro salvos'))
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o e-mail'}`));
  };

  // Por que esta pessoa recebeu menos que o mês cheio? É a pergunta que o
  // cliente devolve por e-mail no dia seguinte — então vai junto na relação.
  const motivoDaLinha = (c) => {
    if (c.modalidade === 'fixo') return '';
    const partes = [];
    (c.alertas || []).forEach((a) => {
      if (['ferias', 'afastamento', 'novo', 'desligado'].includes(a.tipo)) {
        partes.push(a.texto.replace(' — descontado', '').replace(' — proporcional', ''));
      }
    });
    if (c.descontar) {
      const rot = c.descontar > 0
        ? `${c.descontar} dia(s) descontado(s)`
        : `${-c.descontar} dia(s) acrescentado(s)`;
      partes.push(c.desconto_obs ? `${rot}: ${c.desconto_obs}` : rot);
    }
    if (c.dias_fixos != null && !c.descontar) {
      partes.push(`${c.dias_fixos} dias no mês (jornada combinada)`);
    }
    // sobrou diferença sem explicação: melhor dizer que é proporcional do que
    // deixar o cliente achando que é erro
    if (!partes.length && c.dias < c.dias_uteis) {
      partes.push(`${c.dias} de ${c.dias_uteis} dias úteis`);
    }
    return partes.join(' · ');
  };

  // a relação do e-mail é a MESMA da tela: uma linha por colaborador/operadora
  // só as linhas MARCADAS como recarregadas vão ao financeiro (Gauss, 20/10/2026:
  // o e-mail levava todo mundo com valor, mesmo quem não foi recarregado), com o
  // valor que foi de fato recarregado
  const linhasDoEmail = () => todasLinhas
    .filter(({ c }) => c.pagamento && (c.pagamento.valor ?? c.total) > 0)
    .map(({ c, op }) => ({
      nome: c.nome, dias: c.pagamento.dias ?? c.dias, valor: c.pagamento.valor ?? c.total,
      motivo: motivoDaLinha(c),
      operadora: [op.nome, c.linha].filter(Boolean).join(' · '),
      operadora_id: op.id,
    }));
  // ajuste do boleto por unidade (repasse + taxa − desconto): com fechamento lançado,
  // é ele que se rateia entre os colaboradores de cada unidade (pode ser negativo)
  const ajustesUnidade = (dados?.operadoras || [])
    .filter(op => op.fechamento && (op.fechamento.desconto || op.fechamento.repasse || op.fechamento.servico))
    .map(op => ({ nome: op.nome, valor: (op.fechamento.repasse || 0) + (op.fechamento.servico || 0) - (op.fechamento.desconto || 0) }));
  const fmtAjuste = (v) => `${v < 0 ? '−' : '+'}${brl(Math.abs(v))}`;

  const corpoEmail = (extra) => ({
    mes, colaboradores: linhasDoEmail(), boleto_ids: boletoSel,
    destinatarios: boletoForm.emails, copia: boletoForm.copia,
    vencimento: boletoForm.vencimento,
    // rateada por colaborador SÓ no relatório — nunca no arquivo da plataforma
    taxa_boleto: boletoForm.taxa_boleto,
    // planilha de Contas a Pagar da Omie anexada junto (só os marcados)
    anexar_omie: boletoForm.anexar_omie ?? !!dados?.omie_configurada,
    // a régua da competência: explica no e-mail de onde saiu o nº de dias
    base: {
      dias_uteis: dados?.dias_uteis,
      feriados: (dados?.feriados || [])
        .map(f => `${f.nome} (${f.data.slice(0, 5)})`).join(', '),
    },
    operador_id: boletoOperador || undefined, ...extra,
  });

  const previaBoleto = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/boleto/email/`, corpoEmail({ enviar: false }),
      { headers: headers() })
      .then((res) => setBoletoPrevia(res.data.previa))
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível montar o e-mail'}`));
  };

  const enviarBoleto = () => {
    setEnviandoBoleto(true);
    axios.post(`${API}/api/bpo-vt/${clienteId}/boleto/email/`, corpoEmail({
      assunto: boletoPrevia?.assunto, corpo_html: boletoPrevia?.corpo_html, enviar: true,
    }), { headers: headers() })
      .then((res) => {
        setMensagem(`✅ ${res.data.mensagem}`);
        setBoletoPrevia(null);
        carregarBoleto();
      })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível enviar'}`))
      .finally(() => setEnviandoBoleto(false));
  };

  // trocar de mês invalida a prévia e os anexos daquela competência
  useEffect(() => { setBoletoPrevia(null); setBoletoSel([]); }, [mes]);

  // Troca de cliente zera TUDO do boleto: destinatário, cópia, vencimento,
  // remetente, anexos selecionados e prévia. Sem isto, dado de um cliente
  // aparece (e é enviado) no contexto de outro.
  useEffect(() => {
    setBoletoAberto(false);
    setBoletoDados(null);
    setBoletoPrevia(null);
    setBoletoSel([]);
    setBoletoOperador('');
    setBoletoForm({ emails: '', copia: '', vencimento: '' });
  }, [clienteId]);

  // ─── planilha de controle por e-mail (aba 'Controle' da Rubeus): uma linha
  //     por PESSOA — quem tem duas linhas/cartões soma num custo só ───
  const linhasPlanilha = () => {
    const porPessoa = {};
    todasLinhas.filter(({ c }) => c.total > 0).forEach(({ c }) => {
      const k = c.convenia_id || c.nome;
      const p = porPessoa[k];
      if (p) { p.linhas += 1; return; }
      porPessoa[k] = {
        linhas: 1, nome: c.nome, cpf: c.cpf || '', departamento: c.departamento || '',
        centro_custo: c.centro_custo || '', dias: c.dias,
        valor_unitario: c.modalidade === 'fixo' ? null : c.valor_unitario,
        valor_dia: c.valor_dia_pessoa, custo: c.total_pessoa,
      };
    });
    // valor unitário só faz sentido com uma linha de passagem
    return Object.values(porPessoa).map(({ linhas, ...p }) => (
      linhas > 1 ? { ...p, valor_unitario: null } : p));
  };

  const corpoPlanilha = (extra) => ({
    mes, colaboradores: linhasPlanilha(),
    destinatarios: boletoForm.emails, copia: boletoForm.copia,
    operador_id: boletoOperador || undefined, ...extra,
  });

  const abrirPlanilha = () => {
    setPlanilhaAberto(true);
    setPlanilhaPrevia(null);
    carregarBoleto().catch(() => setMensagem('❌ Não foi possível carregar os destinatários'));
  };

  const baixarPlanilha = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/planilha/email/`, corpoPlanilha({ baixar: true }),
      { headers: headers(), responseType: 'blob' })
      .then((res) => {
        const cd = res.headers['content-disposition'] || '';
        const m = cd.match(/filename="([^"]+)"/);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = m ? m[1] : `vt_${mes}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => setMensagem('❌ Não foi possível gerar a planilha'));
  };

  const previaPlanilha = () => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/planilha/email/`, corpoPlanilha({ enviar: false }),
      { headers: headers() })
      .then((res) => setPlanilhaPrevia(res.data.previa))
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível montar o e-mail'}`));
  };

  useEffect(() => {
    if (planilhaAberto && boletoDados && !planilhaPrevia && linhasPlanilha().length) previaPlanilha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planilhaAberto, boletoDados]);

  const trocarEnvioVt = (envio) => {
    axios.post(`${API}/api/bpo-vt/${clienteId}/planilha/email/`, { acao: 'config', envio },
      { headers: headers() })
      .then((res) => {
        setMensagem(`✅ ${res.data.mensagem}`);
        setDados(d => (d ? { ...d, envio_vt: res.data.envio } : d));
      })
      .catch(() => setMensagem('❌ Não foi possível salvar a forma de envio'));
  };

  const enviarPlanilha = () => {
    setEnviandoPlanilha(true);
    axios.post(`${API}/api/bpo-vt/${clienteId}/planilha/email/`, corpoPlanilha({
      assunto: planilhaPrevia?.assunto, corpo_html: planilhaPrevia?.corpo_html, enviar: true,
    }), { headers: headers() })
      .then((res) => {
        setMensagem(`✅ ${res.data.mensagem}`);
        setPlanilhaPrevia(null);
        setPlanilhaAberto(false);
      })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível enviar'}`))
      .finally(() => setEnviandoPlanilha(false));
  };

  const semVt = dados?.sem_vt || [];

  // todas as linhas da competência, de todas as operadoras — base dos botões
  // "marcar todos" / "desmarcar todos"
  const todasLinhas = (dados?.operadoras || [])
    .flatMap(op => op.colaboradores.map(c => ({ c, op })));
  const aMarcar = todasLinhas.filter(({ c }) => c.total > 0 && !c.pagamento);
  const aDesmarcar = todasLinhas.filter(({ c }) => c.pagamento);

  // ══════════════════════════════════════════════════════════════════════
  return (
    <div className="bporh-admissoes-page">
      {configAberto && clienteId && (
        <ConfigBeneficioModal tipo="vt" clienteId={clienteId} mes={mes} cliente={dados?.cliente}
          onFechar={() => setConfigAberto(false)} onSalvo={() => carregar(true)} />
      )}
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">

        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <i className="fas fa-bus header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Vale Transporte
              {dados?.cliente && <span className="text-muted"> · {dados.cliente}</span>}
            </h2>
            {clienteId && (
              <span className="badge d-inline-flex align-items-center gap-2"
                style={{ background: '#00205b', fontSize: '.9rem', padding: '.5rem .8rem' }}
                title="A recarga é paga no dia 25 e o colaborador usa no mês seguinte; o desconto sai na folha do mês do pagamento">
                <i className="fas fa-calendar-days"></i>
                recarga de <b style={{ textTransform: 'capitalize' }}>{mesExtenso(dados?.mes_uso || mesDepois(mes))}</b>
                <span style={{ opacity: .85 }}>
                  · comprada em <b style={{ textTransform: 'capitalize' }}>{mesExtenso(mes)}</b>
                  {mes === mesAtual() && (
                    <span style={{ color: '#f1be48', fontWeight: 700 }}> · este mês</span>
                  )}
                  {' '}· desconto na folha de{' '}
                  <b style={{ textTransform: 'capitalize' }}>{mesExtenso(mes)}</b>
                </span>
              </span>
            )}
            {dados && (
              <span className="badge d-inline-flex align-items-center gap-2"
                style={{ background: '#fff5da', color: '#8a6d00',
                  border: '1px solid #f0dfa8', fontSize: '.85rem',
                  padding: '.45rem .7rem' }}
                title={(dados.feriados || []).length
                  ? 'Feriados já descontados dos dias úteis: '
                    + dados.feriados.map(f => `${f.nome} (${f.data})`).join(', ')
                  : 'Nenhum feriado nesta competência'}>
                <i className="fas fa-calendar-days"></i>
                <b>{dados.dias_uteis} {dados.regra_dias_modo && dados.regra_dias_modo !== 'uteis' ? 'dias' : 'dias úteis'}</b>
                {dados.regra_dias_modo && dados.regra_dias_modo !== 'uteis' && (
                  <span style={{ opacity: .85 }}> ({dados.regra_dias_rotulo})</span>
                )}
                <span style={{ opacity: .85 }}> em {mesExtenso(dados.mes_uso || mesDepois(mes))}</span>
                {(dados.feriados || []).length > 0 && (
                  <span>
                    · −{dados.feriados.length} feriado{dados.feriados.length > 1 ? 's' : ''}
                    {' '}({dados.feriados.map(f => `${f.nome} ${f.data.slice(0, 5)}`).join(', ')})
                  </span>
                )}
                <button type="button" className="btn btn-link p-0"
                  style={{ color: '#8a6d00', fontSize: '.8rem', textDecoration: 'none' }}
                  onClick={() => setFeriadosAberto(true)}
                  title="Cadastrar/editar feriados (municipais, estaduais e nacionais) — mesmo calendário do Controle de VA">
                  <i className="fas fa-pen-to-square"></i>
                </button>
              </span>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            {clienteId && (
              <input type="month" className="form-control form-control-sm" style={{ width: 'auto' }}
                value={mes} onChange={(e) => setParam('mes', e.target.value)}
                aria-label="Competência" />
            )}
            {clienteId && dados && (
              <button className="btn btn-sm btn-outline-dark" onClick={() => setConfigAberto(true)}
                title={`Valor de cada linha, reajuste de tarifa e dias que contam (hoje: ${dados.regra_dias_rotulo || 'dias úteis'})`}>
                <i className="fas fa-sliders me-1"></i>Configurar
              </button>
            )}
            {clienteId && dados && dados.envio_vt !== 'planilha' && (
              <button className="btn btn-sm btn-outline-secondary" onClick={abrirPlanilha}
                title="Enviar ao cliente, por e-mail, a planilha de controle desta competência">
                <i className="fas fa-file-excel me-1"></i>Planilha por e-mail
              </button>
            )}
            {clienteIdRota
              ? <Link to="/BpoRh/ValeTransporte" className="btn btn-sm btn-outline-secondary">
                  <i className="fas fa-arrow-left me-1"></i>Empresas
                </Link>
              : clienteId && (
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setParam('cliente', '')}>
                  <i className="fas fa-arrow-left me-1"></i>Empresas
                </button>
              )}
          </div>
        </div>

        {/* ─── ENTRADA: os portais de VT de todas as empresas, no mesmo formato
             de /BpoRh/Vale Transporte — abra o portal daqui ou clique em
             Controle para montar o mês ─── */}
        {!clienteId && (carregandoPortais ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="text-muted mt-2">Carregando os portais de vale transporte...</p>
          </div>
        ) : (
          <div className="table-responsive bg-white rounded shadow-sm">
            <table className="table table-striped table-hover align-middle mb-0">
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th><i className="fas fa-building me-2"></i>Cliente</th>
                  <th><i className="fas fa-user-tie me-2"></i>Operador</th>
                  <th><i className="fas fa-tv me-2"></i>Propriedade</th>
                  <th className="text-center"><i className="fas fa-globe me-2"></i>Portal</th>
                  <th><i className="fas fa-user me-2"></i>Login</th>
                  <th><i className="fas fa-key me-2"></i>Senha</th>
                  <th><i className="fas fa-envelope me-2"></i>E-mail reset</th>
                  <th className="text-center">Controle</th>
                </tr>
              </thead>
              <tbody>
                {portais.map((s) => (
                  <tr key={s.id}>
                    <td className="fw-bold">{s.cliente}</td>
                    <td>{s.operador || '-'}</td>
                    <td>{s.propriedade}</td>
                    <td className="text-center">
                      {s.portal ? (
                        <a href={s.portal} target="_blank" rel="noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          title={`Abrir o portal ${s.propriedade} em nova aba`}>
                          <i className="fas fa-arrow-up-right-from-square me-1"></i>Abrir
                        </a>
                      ) : (
                        <span className="badge bg-light text-secondary border"
                          title="Esta empresa não tem portal de VT — o benefício é pago como valor fixo (Mobilidade), direto na folha ou por boleto.">
                          sem portal
                        </span>
                      )}
                    </td>
                    <td>
                      {s.login ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <span className="small">{s.login}</span>
                          <button className="btn btn-sm btn-light p-1 border"
                            onClick={() => copiar(s.login, 'Login')} title="Copiar login">
                            <i className="fas fa-copy text-muted" style={{ fontSize: '.72rem' }}></i>
                          </button>
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td>
                      {s.tem_senha ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <code>{senhasPortal[s.id] != null ? senhasPortal[s.id] : '••••••••'}</code>
                          <button className="btn btn-sm btn-light p-1 border"
                            onClick={() => revelarSenha(s.id)}
                            title="Revelar e copiar a senha (o acesso fica registrado)">
                            <i className={`fas ${senhasPortal[s.id] != null ? 'fa-copy' : 'fa-eye'} text-muted`}
                              style={{ fontSize: '.72rem' }}></i>
                          </button>
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td className="small">{s.email_reset || '-'}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-success"
                        onClick={() => setParam('cliente', String(s.cliente_id))}
                        title={`Montar o controle de VT da ${s.cliente} (colaboradores, valores, dias e recarga)`}>
                        <i className="fas fa-bus me-1"></i>Controle
                      </button>
                    </td>
                  </tr>
                ))}
                {portais.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">
                    <i className="fas fa-inbox me-2"></i>
                    Nenhum vale transporte cadastrado — cadastre o serviço (categoria
                    Vale Transporte) na página <Link to="/BpoRhClientesCadastro">Clientes</Link>.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        ))}

        {/* ─── A EMPRESA ─── */}
        {clienteId && carregando && (
          <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
        )}

        {clienteId && !carregando && dados && (
          <>
            <TravaBeneficio clienteId={clienteId} tipo="vt" mes={mes} trava={dados.trava}
              onMudou={() => carregar(true)} />

            {/* portais + resumo */}
            <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
              {(dados.portais || []).map(p => (
                <a key={p.id} href={p.portal} target="_blank" rel="noreferrer"
                  className="btn btn-sm btn-outline-primary"
                  title={p.login ? `Login: ${p.login}` : 'Abrir o portal'}>
                  <i className="fas fa-arrow-up-right-from-square me-1"></i>{p.propriedade}
                </a>
              ))}
              <div className="ms-auto d-flex gap-2 flex-wrap align-items-center">
                {dados.envio_vt === 'planilha' ? (
                  <button className="btn btn-sm btn-outline-primary" onClick={abrirPlanilha}
                    title="Mandar ao cliente a planilha de controle desta competência (sem boleto)">
                    <i className="fas fa-file-excel me-1"></i>Planilha por e-mail
                  </button>
                ) : (
                  <button className="btn btn-sm btn-outline-primary" onClick={abrirBoleto}
                    title="Anexar o boleto da recarga e mandar ao financeiro do cliente, com a relação dos colaboradores">
                    <i className="fas fa-file-invoice-dollar me-1"></i>Boleto e e-mail
                  </button>
                )}
                {temMobilidade && (
                  <button className="btn btn-sm" style={{ background: '#f1be48', color: '#3a2c00', fontWeight: 600 }}
                    onClick={baixarSwile}
                    title="Arquivo de recarga da Swile só com as unidades de mobilidade: Nome, CPF e o valor na coluna Mobilidade (os marcados; sem marcados, todos com valor)">
                    <i className="fas fa-file-arrow-down me-1"></i>Arquivo Swile
                  </button>
                )}
                <button className="btn btn-sm btn-outline-primary" onClick={abrirOmieVt}
                  title="Planilha de importação de Contas a Pagar da Omie, só com os colaboradores marcados">
                  <i className="fas fa-file-export me-1"></i>Planilha Omie
                </button>
                <button className="btn btn-sm btn-outline-success"
                  onClick={() => marcarPago(aMarcar.map(({ c, op }) => itemPgto(c, op)), true)}
                  disabled={travado || salvandoPgto || !aMarcar.length}
                  title={aMarcar.length
                    ? `Marcar como recarregado ${aMarcar.length} colaborador(es) em todas as operadoras`
                    : 'Todos já estão marcados (ou não há valor a recarregar)'}>
                  <i className="fas fa-check-double me-1"></i>
                  Marcar todos{aMarcar.length ? ` (${aMarcar.length})` : ''}
                </button>
                {/* desmarcar APAGA o registro de recarga (valor, dias e quem
                    marcou), então confirma em duas etapas — igual às outras telas */}
                {aDesmarcar.length > 0 && (confirmandoDesmarcar ? (
                  <span className="d-inline-flex align-items-center gap-1">
                    <span className="small text-muted">
                      Apagar a recarga de {aDesmarcar.length}?
                    </span>
                    <button className="btn btn-sm btn-danger"
                      disabled={salvandoPgto}
                      onClick={() => {
                        marcarPago(aDesmarcar.map(({ c, op }) => itemPgto(c, op)), false);
                        setConfirmandoDesmarcar(false);
                      }}>Confirmar</button>
                    <button className="btn btn-sm btn-light border"
                      onClick={() => setConfirmandoDesmarcar(false)}>Cancelar</button>
                  </span>
                ) : (
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setConfirmandoDesmarcar(true)} disabled={travado || salvandoPgto}
                    title={`Desmarcar ${aDesmarcar.length} — apaga o registro da recarga`}>
                    <i className="fas fa-rotate-left me-1"></i>
                    Desmarcar todos ({aDesmarcar.length})
                  </button>
                ))}
                <span className="badge bg-light text-dark border p-2">
                  {dados.total_colaboradores} com VT
                </span>
                <span className="badge p-2" style={{ background: '#e6f7ef', color: '#116b47' }}>
                  {dados.pagos} recarregado(s) · {brl(dados.total_pago)}
                </span>
                <span className="badge p-2" style={{ background: '#00205b', fontSize: '.95rem' }}>
                  TOTAL {brl(dados.total_geral)}
                </span>
              </div>
            </div>

            <div className="row g-3">
              {/* blocos por operadora */}
              <div className="col-lg-9">
                {(dados.operadoras || []).map(op => (
                  <div className="card mb-3 shadow-sm" key={op.id}>
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
                      style={{ background: '#00205b', color: '#fff' }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <i className="fas fa-bus"></i>
                        <b>{op.nome}</b>
                        {op.empresa && (
                          <span className="badge bg-light text-dark">{op.empresa}</span>
                        )}
                        {op.cnpj && (
                          <span className="small" style={{ opacity: .85 }}>
                            <i className="fas fa-building me-1"></i>{op.cnpj}
                          </span>
                        )}
                        <button className="btn btn-sm btn-link p-0 ms-1"
                          style={{ color: '#f1be48' }}
                          onClick={() => setEditarUnidade({
                            id: op.id, nome: op.nome,
                            empresa: op.empresa || '', cnpj: op.cnpj || '',
                          })}
                          title="Empresa e CNPJ desta unidade (a Gauss emite um boleto por CNPJ)">
                          <i className="fas fa-pen" style={{ fontSize: '.75rem' }}></i>
                        </button>
                        <span className="badge bg-light text-dark">
                          {op.pessoas} pessoa{op.pessoas === 1 ? '' : 's'}
                          {op.qtd !== op.pessoas ? ` · ${op.qtd} linhas` : ''}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ color: '#f1be48', fontWeight: 700 }}>{brl(op.total_boleto)}</span>
                        <button disabled={travado} className="btn btn-sm btn-light"
                          onClick={() => abrirIncluir(op)}
                          title="Incluir uma linha nesta unidade">
                          <i className="fas fa-user-plus"></i>
                        </button>
                        <button className="btn btn-sm btn-light"
                          onClick={() => marcarPago(op.colaboradores.filter(c => c.total > 0).map(c => itemPgto(c, op)), true)}
                          disabled={travado || salvandoPgto || !op.qtd}
                          title="Marcar toda a unidade como recarregada">
                          <i className="fas fa-check-double"></i>
                        </button>
                        <button disabled={travado} className="btn btn-sm btn-light text-danger"
                          onClick={() => setOperadoraExcluir(op)}
                          title="Excluir esta unidade">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    {/* fechamento do boleto da unidade: o que a operadora soma
                        ou abate em cima do pedido */}
                    <div className="d-flex flex-wrap align-items-end gap-3 px-3 py-2"
                      style={{ background: '#f6f8fb', borderBottom: '2px solid #00205b' }}>
                      <span className="fw-bold align-self-center me-1"
                        style={{ color: '#00205b', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-file-invoice-dollar me-2"></i>
                        Fechamento do boleto
                      </span>
                      {[
                        ['desconto', 'Desconto', '−', '#a11d2b',
                          'Abatido do pedido (crédito do mês anterior, devolução)'],
                        ['repasse', 'Repasse', '+', '#116b47', 'Somado ao pedido'],
                        ['servico', 'Taxa de serviço', '+', '#116b47',
                          'Taxa cobrada no boleto — somada ao pedido e rateada no relatório do e-mail'],
                      ].map(([campo, rotulo, sinal, cor, dica]) => (
                        <div key={campo}>
                          <label className="form-label fw-semibold mb-1"
                            style={{ fontSize: '.8rem', color: '#00205b' }}>
                            <span style={{ color: cor, fontWeight: 700 }}>{sinal}</span> {rotulo}
                          </label>
                          <div className="input-group input-group-sm" style={{ width: 140 }}>
                            <span className="input-group-text"
                              style={{ background: '#fff', color: '#6c757d' }}>R$</span>
                            <input type="text" className="form-control text-end fw-semibold"
                              style={op.fechamento[campo]
                                ? { borderColor: '#f1be48', background: '#fffdf4' } : undefined}
                              defaultValue={num(op.fechamento[campo]) || ''}
                              key={`f-${campo}-${op.id}-${op.fechamento[campo]}`}
                              placeholder="0,00"
                              onBlur={(e) => {
                                if (e.target.value === (num(op.fechamento[campo]) || '')) return;
                                salvarFechamento(op, { [campo]: e.target.value || 0 });
                              }}
                              title={dica} />
                          </div>
                        </div>
                      ))}
                      <div className="ms-auto text-end">
                        <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                          {op.pedido_marcados
                            ? <>pedido dos marcados ({op.pedido_marcados}) {brl(op.total_pedido)}</>
                            : <span title="Ninguém marcado como recarregado nesta unidade ainda: prévia com todos">pedido (prévia, todos) {brl(op.total_pedido)}</span>}
                          {op.fechamento.desconto ? ` − ${brl(op.fechamento.desconto)}` : ''}
                          {op.fechamento.repasse ? ` + ${brl(op.fechamento.repasse)}` : ''}
                          {op.fechamento.servico ? ` + ${brl(op.fechamento.servico)}` : ''}
                        </div>
                        <div className="fw-bold px-2 py-1 rounded d-inline-block"
                          style={{ fontSize: '1.05rem', color: '#00205b',
                            background: '#fff', border: '1px solid #d6dee8' }}>
                          TOTAL BOLETO <span style={{ color: '#116b47' }}>{brl(op.total_boleto)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle mb-0"
                        style={{ minWidth: 1180 }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 34 }} title="Recarga feita"></th>
                            <th style={{ minWidth: 190 }}>Colaborador</th>
                            <th style={{ width: 150 }}>Linha / cartão</th>
                            <th className="text-center" style={{ width: 110 }}
                              title="Tarifa da passagem, ou o valor fixo do mês quando a linha é de valor fixo">
                              Valor
                            </th>
                            <th className="text-center" style={{ width: 80 }}>Qntd</th>
                            <th className="text-center" style={{ width: 80 }}
                              title="Dias úteis já descontando feriado, férias e o proporcional de admissão/desligamento">
                              Dias úteis
                            </th>
                            <th className="text-center" style={{ width: 90 }}>A descontar</th>
                            <th className="text-center" style={{ width: 70 }}>Dias</th>
                            <th className="text-end" style={{ width: 110 }}>Total linha</th>
                            <th className="text-end" style={{ width: 110 }}>Total pessoa</th>
                            <th className="text-end" style={{ width: 100 }}
                              title="Valor por dia somando todas as linhas da pessoa">
                              Diário/pessoa
                            </th>
                            <th style={{ width: 150 }}>Departamento</th>
                            <th style={{ width: 150 }}>Centro de custo</th>
                            <th style={{ minWidth: 180 }}>Obs.</th>
                            <th style={{ width: 150 }}
                              title="Muda a linha deste colaborador para outra unidade/operadora">Unidade</th>
                            <th style={{ width: 40 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {op.colaboradores.map(c => (
                            <tr key={c.valor_id}
                              style={c.pagamento ? { background: '#f2fbf6' } : undefined}>
                              <td className="text-center">
                                <input type="checkbox" className="form-check-input"
                                  checked={!!c.pagamento} disabled={travado || salvandoPgto}
                                  onChange={(e) => marcarPago([itemPgto(c, op)], e.target.checked)}
                                  title={c.pagamento
                                    ? `Recarregado em ${c.pagamento.pago_em}${c.pagamento.pago_por ? ' por ' + c.pagamento.pago_por : ''}`
                                    : 'Marcar como recarregado'} />
                              </td>
                              <td>
                                <div className="fw-semibold">{c.nome}</div>
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  {c.vinculo && c.vinculo !== '—' && (
                                    <span className="badge bg-light text-dark border">{c.vinculo}</span>
                                  )}
                                  {(c.alertas || []).map((a, i) => {
                                    const e = COR_ALERTA[a.tipo] || COR_ALERTA.experiencia;
                                    return (
                                      <span key={i} className="badge"
                                        style={{ background: e.bg, color: e.cor, fontWeight: 600 }}
                                        title={a.texto}>
                                        <i className={`fas ${e.icone} me-1`}></i>
                                        {a.texto.length > 40 ? a.texto.slice(0, 40) + '…' : a.texto}
                                      </span>
                                    );
                                  })}
                                </div>
                              </td>
                              <td>
                                <input disabled={travado} type="text" className="form-control form-control-sm"
                                  defaultValue={c.linha || ''}
                                  key={`ln-${c.valor_id}`}
                                  onBlur={(e) => {
                                    if (e.target.value === (c.linha || '')) return;
                                    salvarValor(baseValor(c, op, { linha: e.target.value }));
                                  }}
                                  placeholder="50294 | BH BUS"
                                  title="Linha e cartão, como na planilha da operadora" />
                                <select disabled={travado} className="form-select form-select-sm mt-1"
                                  style={{ fontSize: '.72rem', padding: '.1rem .4rem' }}
                                  value={c.modalidade || 'passagem'}
                                  onChange={(e) => salvarValor(
                                    baseValor(c, op, { modalidade: e.target.value }))}
                                  title="Passagem multiplica tarifa × quantidade × dias. Valor fixo é um valor mensal fechado (Mobilidade).">
                                  <option value="passagem">passagem/dia</option>
                                  <option value="fixo">valor fixo/mês</option>
                                </select>
                              </td>
                              <td className="text-center">
                                <input disabled={travado} type="text" className="form-control form-control-sm text-center"
                                  defaultValue={num(c.valor_unitario)}
                                  key={`v-${c.valor_id}-${c.valor_unitario}`}
                                  onBlur={(e) => {
                                    if (e.target.value === num(c.valor_unitario)) return;
                                    salvarValor(baseValor(c, op, { valor_unitario: e.target.value }));
                                  }}
                                  title="Preço de uma passagem" />
                              </td>
                              <td className="text-center">
                                {c.modalidade === 'fixo' ? (
                                  <span className="text-muted" title="Valor fixo não multiplica">—</span>
                                ) : (
                                  <input disabled={travado} type="text" className="form-control form-control-sm text-center"
                                    defaultValue={c.quantidade}
                                    key={`q-${c.valor_id}-${c.quantidade}`}
                                    onBlur={(e) => {
                                      if (String(e.target.value) === String(c.quantidade)) return;
                                      salvarValor(baseValor(c, op, { quantidade: e.target.value }));
                                    }}
                                    title="Passagens por dia — 2 é ida e volta" />
                                )}
                              </td>
                              <td className="text-center">
                                <span style={{ color: ORIGEM_DIAS[c.dias_origem]?.cor }}
                                  title={`${ORIGEM_DIAS[c.dias_origem]?.rotulo || ''}`
                                    + ` — ${c.dias_uteis} dias úteis na competência.`}>
                                  {c.dias_base}
                                </span>
                                {c.dias_origem === 'fixo' && (
                                  <div className="small" style={{ color: ORIGEM_DIAS.fixo.cor }}>fixo</div>
                                )}
                              </td>
                              <td className="text-center">
                                <input disabled={travado} type="text" className="form-control form-control-sm text-center"
                                  defaultValue={c.descontar || ''}
                                  key={`ds-${c.valor_id}-${c.descontar}`}
                                  onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if (String(v || 0) === String(c.descontar || 0)) return;
                                    salvarAjuste(c, op, { descontar: v === '' ? 0 : v });
                                  }}
                                  style={c.descontar ? { borderColor: '#b5560e' } : undefined}
                                  title="Dias a descontar nesta competência (reembolso do mês anterior, depósito etc.). Explique na coluna Obs." />
                              </td>
                              <td className="text-center fw-bold">
                                {c.modalidade === 'fixo' ? (
                                  <span className="text-muted fw-normal"
                                    title="Valor fixo do mês — os dias são informação, não entram na conta">
                                    {c.dias} · fixo
                                  </span>
                                ) : (
                                  <input disabled={travado} type="text" className="form-control form-control-sm text-center fw-bold"
                                    style={{ width: 56,
                                      borderColor: c.descontar ? '#b5560e' : undefined }}
                                    defaultValue={c.dias}
                                    key={`di-${c.valor_id}-${c.dias}`}
                                    onBlur={(e) => {
                                      const v = e.target.value.trim();
                                      if (v === String(c.dias)) return;
                                      if (v !== '' && !/^\d{1,2}$/.test(v)) return;
                                      // o número absoluto vira desconto/acréscimo
                                      // sobre a base — é o mesmo registro do
                                      // "a descontar", com o motivo ao lado
                                      salvarAjuste(c, op, {
                                        descontar: v === '' ? 0 : c.dias_base - Number(v),
                                      });
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    title={'Dias desta linha na competência. Digite o número final '
                                      + '(a diferença vira o "a descontar"); apague para voltar '
                                      + `ao calculado (${c.dias_base}).`} />
                                )}
                              </td>
                              <td className="text-end fw-semibold">{brl(c.total)}</td>
                              <td className="text-end text-muted">{brl(c.total_pessoa)}</td>
                              <td className="text-end text-muted">
                                {c.valor_dia_pessoa ? brl(c.valor_dia_pessoa) : '—'}
                              </td>
                              <td>
                                <input disabled={travado} type="text" className="form-control form-control-sm"
                                  defaultValue={c.departamento || ''}
                                  key={`dp-${c.valor_id}-${c.departamento || ''}`}
                                  onBlur={(e) => {
                                    if (e.target.value === (c.departamento || '')) return;
                                    salvarValor(baseValor(c, op, { departamento: e.target.value }));
                                  }}
                                  style={c.departamento_ov ? { borderColor: '#0a6ea1' } : undefined}
                                  title={c.departamento_ov
                                    ? 'Rótulo próprio do cliente — apague para voltar ao da Convenia'
                                    : 'Vem da Convenia. Digite para usar o rótulo do rateio do cliente.'} />
                              </td>
                              <td>
                                <input disabled={travado} type="text" className="form-control form-control-sm"
                                  defaultValue={c.centro_custo || ''}
                                  key={`cc-${c.valor_id}-${c.centro_custo || ''}`}
                                  onBlur={(e) => {
                                    if (e.target.value === (c.centro_custo || '')) return;
                                    salvarValor(baseValor(c, op, { centro_custo: e.target.value }));
                                  }}
                                  style={c.centro_custo_ov ? { borderColor: '#0a6ea1' } : undefined}
                                  title={c.centro_custo_ov
                                    ? 'Rótulo próprio do cliente — apague para voltar ao da Convenia'
                                    : 'Vem da Convenia. Digite para usar o CC do rateio do cliente.'} />
                              </td>
                              <td>
                                <input disabled={travado} type="text" className="form-control form-control-sm"
                                  defaultValue={c.desconto_obs || ''}
                                  key={`ob-${c.valor_id}-${c.desconto_obs || ''}`}
                                  onBlur={(e) => {
                                    if (e.target.value === (c.desconto_obs || '')) return;
                                    salvarAjuste(c, op, { obs: e.target.value });
                                  }}
                                  placeholder="motivo do desconto"
                                  title="Vai junto no controle e explica o desconto de dias" />
                              </td>
                              <td>
                                {/* trocar de unidade: a linha vai inteira (valor, dias e ajustes) */}
                                <select className="form-select form-select-sm" value={op.id}
                                  disabled={travado || (dados.operadoras || []).length < 2}
                                  onChange={(e) => {
                                    const destino = (dados.operadoras || []).find(o => String(o.id) === e.target.value);
                                    if (!destino || destino.id === op.id) return;
                                    salvarValor(baseValor(c, op, { operadora_id: destino.id }),
                                      `✅ ${c.nome} passou para ${destino.nome}`);
                                  }}
                                  title="Mudar esta linha para outra unidade/operadora">
                                  {(dados.operadoras || []).map(o => (
                                    <option key={o.id} value={o.id}>{o.nome}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                <button disabled={travado} className="btn btn-sm btn-light text-primary p-1 me-1"
                                  onClick={() => abrirOutraLinha(op, c)}
                                  title={`Adicionar outra linha/cartão para ${c.nome} (ex.: mais de um ônibus)`}>
                                  <i className="fas fa-plus"></i>
                                </button>
                                <button disabled={travado} className="btn btn-sm btn-light text-danger p-1"
                                  onClick={() => setRemover({ colab: c, operadora: op })}
                                  title="Tirar esta linha do colaborador">
                                  <i className="fas fa-user-minus"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                          {!op.colaboradores.length && (
                            <tr>
                              <td colSpan={16} className="text-center text-muted py-3">
                                Nenhuma linha cadastrada nesta unidade ainda.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td colSpan={8} className="text-end fw-bold">TOTAL PEDIDO</td>
                            <td className="text-end fw-bold">{brl(op.total_pedido)}</td>
                            <td colSpan={6}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                  </div>
                ))}

                <DiferencaMesAnterior url={`${API}/api/bpo-vt/${clienteId}/`} mes={mes}
                  atual={dados} extrair={pessoasVt} rotulo="VT" />

                {/* nova operadora */}
                <div className="card shadow-sm">
                  <div className="card-body d-flex align-items-center gap-2 flex-wrap">
                    <i className="fas fa-plus text-muted"></i>
                    <input className="form-control form-control-sm" style={{ maxWidth: 260 }}
                      placeholder="Nova operadora (ex.: TEU, TRI, Cartão LEGAL)"
                      value={novaOperadora}
                      onChange={(e) => setNovaOperadora(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && criarOperadora()} />
                    <button className="btn btn-sm btn-outline-primary"
                      onClick={criarOperadora} disabled={salvandoOper || !novaOperadora.trim()}>
                      Criar
                    </button>
                    <span className="text-muted small">
                      cada operadora vira um bloco, como na planilha
                    </span>
                  </div>
                </div>
              </div>

              {/* quem ainda não tem VT */}
              <div className="col-lg-3">
                <div className="card shadow-sm">
                  <div className="card-header bg-white">
                    <b>Sem VT</b>
                    <span className="badge bg-light text-dark border ms-2">{semVt.length}</span>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {dados.sem_vt_novos > 0 && (
                        <span className="badge" style={{ background: '#e6f7ef', color: '#116b47' }}>
                          <i className="fas fa-user-plus me-1"></i>{dados.sem_vt_novos} admissão recente
                        </span>
                      )}
                      {dados.sem_vt_ferias > 0 && (
                        <span className="badge" style={{ background: '#e3f2fd', color: '#0a6ea1' }}>
                          <i className="fas fa-umbrella-beach me-1"></i>{dados.sem_vt_ferias} de férias
                        </span>
                      )}
                      {dados.sem_vt_desligados > 0 && (
                        <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b' }}>
                          <i className="fas fa-user-xmark me-1"></i>{dados.sem_vt_desligados} desligado(s)
                        </span>
                      )}
                    </div>
                    <div className="small text-muted mt-2">
                      Quem não recebe vale transporte. Uma admissão nova cai aqui —
                      inclua se ela tiver direito. Férias e desligamento aparecem
                      marcados, na ordem de quem precisa de decisão.
                    </div>
                  </div>
                  <ul className="list-group list-group-flush"
                    style={{ maxHeight: 520, overflowY: 'auto' }}>
                    {semVt.map(c => (
                      <li className="list-group-item small" key={c.convenia_id}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className={c.novo || c.desligado ? 'fw-bold' : ''}>{c.nome}</div>
                            <div className="text-muted">
                              {c.vinculo !== '—' && <span>{c.vinculo}</span>}
                              {c.admissao && <span> · admitido {c.admissao}</span>}
                            </div>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {(c.alertas || []).map((a, i) => {
                                const e = COR_ALERTA[a.tipo] || COR_ALERTA.experiencia;
                                return (
                                  <span key={i} className="badge"
                                    style={{ background: e.bg, color: e.cor, fontWeight: 600 }}
                                    title={a.texto}>
                                    <i className={`fas ${e.icone} me-1`}></i>
                                    {a.texto.length > 34 ? a.texto.slice(0, 34) + '…' : a.texto}
                                  </span>
                                );
                              })}
                              {c.novo && !(c.alertas || []).some(a => a.tipo === 'novo') && (
                                <span className="badge"
                                  style={{ background: '#e6f7ef', color: '#116b47' }}>
                                  <i className="fas fa-user-plus me-1"></i>admissão recente
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                    {!semVt.length && (
                      <li className="list-group-item small text-muted">
                        Todo mundo já está em alguma operadora.
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {/* MODAL — incluir colaborador na operadora */}
        {incluir && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header"
                  style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-user-plus me-2"></i>Incluir em {incluir.nome}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      O colaborador passa a aparecer no controle a partir desta competência
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setIncluir(null)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label small fw-semibold">Colaborador</label>
                  <select className="form-select mb-3" value={formIncluir.convenia_id}
                    onChange={(e) => escolherColaborador(e.target.value)}>
                    <option value="">Escolha…</option>
                    {semVt.map(c => (
                      <option key={c.convenia_id} value={c.convenia_id}>
                        {c.nome}{c.vinculo !== '—' ? ` · ${c.vinculo}` : ''}
                      </option>
                    ))}
                    {/* quem já tem VT pode ganhar outra linha (BH BUS + OTIMO) */}
                    {(dados?.operadoras || [])
                      .flatMap(o => o.colaboradores)
                      .filter((c, i, arr) => arr.findIndex(x => x.convenia_id === c.convenia_id) === i)
                      .map(c => (
                        <option key={`o-${c.convenia_id}`} value={c.convenia_id}>
                          {c.nome} · já tem VT (outra linha)
                        </option>
                      ))}
                  </select>
                  <div className="row g-2 mb-3">
                    <div className="col-7">
                      <label className="form-label small fw-semibold">Linha / cartão</label>
                      <input className="form-control" placeholder="50294 | BH BUS (opcional)"
                        value={formIncluir.linha}
                        onChange={(e) => setFormIncluir(f => ({ ...f, linha: e.target.value }))} />
                    </div>
                    <div className="col-5">
                      <label className="form-label small fw-semibold">Modalidade</label>
                      <select className="form-select" value={formIncluir.modalidade}
                        onChange={(e) => setFormIncluir(f => ({ ...f, modalidade: e.target.value }))}>
                        <option value="passagem">Passagem por dia</option>
                        <option value="fixo">Valor fixo no mês</option>
                      </select>
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-4">
                      <label className="form-label small fw-semibold">Passagens/dia</label>
                      <input className="form-control" value={formIncluir.quantidade}
                        disabled={formIncluir.modalidade === 'fixo'}
                        onChange={(e) => setFormIncluir(f => ({ ...f, quantidade: e.target.value }))} />
                    </div>
                    <div className="col-4">
                      <label className="form-label small fw-semibold">
                        {formIncluir.modalidade === 'fixo' ? 'Valor fixo do mês' : 'Valor unitário'}
                      </label>
                      <input className="form-control"
                        placeholder={formIncluir.modalidade === 'fixo' ? '300,00' : '5,30'}
                        value={formIncluir.valor_unitario}
                        onChange={(e) => setFormIncluir(f => ({ ...f, valor_unitario: e.target.value }))} />
                    </div>
                    <div className="col-4">
                      <label className="form-label small fw-semibold">Dias fixos</label>
                      <input className="form-control" placeholder="opcional"
                        value={formIncluir.dias_fixos}
                        onChange={(e) => setFormIncluir(f => ({ ...f, dias_fixos: e.target.value }))} />
                    </div>
                  </div>
                  <div className="row g-2 mt-1">
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Departamento</label>
                      <input className="form-control" placeholder="da Convenia"
                        value={formIncluir.departamento}
                        onChange={(e) => setFormIncluir(f => ({ ...f, departamento: e.target.value }))} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Centro de custo</label>
                      <input className="form-control" placeholder="da Convenia"
                        value={formIncluir.centro_custo}
                        onChange={(e) => setFormIncluir(f => ({ ...f, centro_custo: e.target.value }))} />
                    </div>
                  </div>
                  <div className="small text-muted mt-2">
                    <b>Departamento</b> e <b>centro de custo</b> vêm preenchidos com o cadastro
                    do colaborador. Troque quando o rateio do cliente usar outro rótulo; em
                    branco, a tela mostra o da Convenia.
                  </div>
                  <div className="small text-muted mt-2">
                    <b>Dias fixos</b> é para quem não vai ao escritório todo dia. Em branco,
                    o sistema usa os dias úteis do mês, já descontando férias, afastamento e
                    o proporcional de admissão ou desligamento.
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setIncluir(null)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={confirmarIncluir}>Incluir</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL — remover colaborador da operadora */}
        {remover && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header"
                  style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-user-minus me-2"></i>Tirar do vale transporte
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {remover.colab.nome} sai de {remover.operadora.nome}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setRemover(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    O cadastro e o ajuste de dias são apagados. As recargas já marcadas
                    como pagas continuam no histórico.
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setRemover(null)}>Cancelar</button>
                  <button className="btn btn-danger" onClick={() => {
                    salvarValor({ id: remover.colab.valor_id, remover: true },
                    `✅ ${remover.colab.nome} saiu de ${remover.operadora.nome}`)
                      .then(() => setRemover(null));
                  }}>Remover</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL — empresa e CNPJ da unidade */}
        {editarUnidade && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header"
                  style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-building me-2"></i>Unidade
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      Cada unidade tem o seu boleto — a Gauss emite um por CNPJ
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => setEditarUnidade(null)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label small fw-semibold">Nome</label>
                  <input className="form-control mb-3" value={editarUnidade.nome}
                    onChange={(e) => setEditarUnidade(u => ({ ...u, nome: e.target.value }))} />
                  <div className="row g-2">
                    <div className="col-5">
                      <label className="form-label small fw-semibold">Empresa</label>
                      <input className="form-control" placeholder="SBC"
                        value={editarUnidade.empresa}
                        onChange={(e) => setEditarUnidade(u => ({ ...u, empresa: e.target.value }))} />
                    </div>
                    <div className="col-7">
                      <label className="form-label small fw-semibold">CNPJ</label>
                      <input className="form-control" placeholder="26.280.256/0003-50"
                        value={editarUnidade.cnpj}
                        onChange={(e) => setEditarUnidade(u => ({ ...u, cnpj: e.target.value }))} />
                    </div>
                  </div>
                  <div className="small text-muted mt-2">
                    O CNPJ é o da filial que recebe a recarga, não o da matriz.
                    Ele vai na planilha e identifica o boleto.
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setEditarUnidade(null)}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={salvarUnidade}>Salvar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL — calendário de feriados (componente compartilhado com o VA) */}
        {feriadosAberto && (
          <CalendarioFeriados
            clienteId={clienteId}
            clienteNome={dados?.cliente}
            mes={mes}
            onFechar={() => setFeriadosAberto(false)}
            onMudou={(msg) => { setMensagem(msg); carregar(true); }}
          />
        )}

        {/* MODAL — boleto da recarga e e-mail ao financeiro */}
        {boletoAberto && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
              overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header"
                  style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-file-invoice-dollar me-2"></i>
                      Boleto do vale transporte
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {dados?.cliente} · recarga de {mesExtenso(dados?.mes_uso || mesDepois(mes))} · pago em {mesExtenso(mes)}
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => { setBoletoAberto(false); setBoletoPrevia(null); }}
                    disabled={enviandoBoleto}></button>
                </div>

                <div className="modal-body">
                  {/* 1. anexo */}
                  <h6 className="fw-bold text-muted border-bottom pb-1 mb-2">
                    <i className="fas fa-paperclip me-2"></i>Boleto em anexo
                  </h6>
                  <input type="file" accept="application/pdf,.pdf" ref={boletoInput}
                    onChange={anexarBoleto} style={{ display: 'none' }} />
                  <button className="btn btn-sm btn-outline-primary"
                    disabled={subindoBoleto}
                    onClick={() => boletoInput.current?.click()}>
                    {subindoBoleto
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                      : <><i className="fas fa-file-arrow-up me-2"></i>Anexar boleto (PDF)</>}
                  </button>

                  {(boletoDados?.boletos || []).map(b => (
                    <div key={b.id} className="d-flex align-items-center gap-2 mt-2 small">
                      <input type="checkbox" className="form-check-input"
                        checked={boletoSel.includes(b.id)}
                        onChange={(e) => setBoletoSel(sel => (e.target.checked
                          ? [...sel, b.id] : sel.filter(x => x !== b.id)))}
                        title="Incluir este boleto no e-mail" />
                      <i className="fas fa-file-pdf" style={{ color: '#a11d2b' }}></i>
                      <a href={`${API}/api/bpo-vt/${clienteId}/boleto/${b.id}/`}
                        target="_blank" rel="noreferrer"
                        style={{ textDecoration: 'none' }}>{b.nome}</a>
                      <span className="text-muted" style={{ fontSize: '.72rem' }}>
                        {b.enviado_em}{b.enviado_por ? ` · ${b.enviado_por}` : ''}
                      </span>
                      <button className="btn btn-sm btn-light border p-1"
                        onClick={() => removerBoleto(b)} title="Remover">
                        <i className="fas fa-trash text-danger" style={{ fontSize: '.7rem' }}></i>
                      </button>
                    </div>
                  ))}
                  {!(boletoDados?.boletos || []).length && (
                    <div className="small text-muted mt-2">
                      Nenhum boleto anexado nesta competência.
                    </div>
                  )}

                  {/* 2. destinatários */}
                  <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                    <i className="fas fa-address-book me-2"></i>Financeiro do cliente
                  </h6>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Para</label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="financeiro@cliente.com.br"
                        value={boletoForm.emails}
                        onChange={(e) => setBoletoForm(f => ({ ...f, emails: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Cópia (opcional)</label>
                      <input type="text" className="form-control form-control-sm"
                        value={boletoForm.copia}
                        onChange={(e) => setBoletoForm(f => ({ ...f, copia: e.target.value }))} />
                    </div>
                  </div>
                  <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
                    <div>
                      <label className="form-label small fw-semibold">Vencimento do boleto</label>
                      <input type="text" className="form-control form-control-sm"
                        style={{ width: '150px' }} placeholder="05/09/2026"
                        value={boletoForm.vencimento}
                        onChange={(e) => setBoletoForm(f => ({ ...f, vencimento: e.target.value }))} />
                    </div>
                    <div className="form-check mb-1">
                      <input className="form-check-input" type="checkbox" id="vtAnexarOmie"
                        checked={boletoForm.anexar_omie ?? !!dados?.omie_configurada}
                        onChange={(e) => { setBoletoForm(f => ({ ...f, anexar_omie: e.target.checked })); setBoletoPrevia(null); }} />
                      <label className="form-check-label small" htmlFor="vtAnexarOmie"
                        title="Anexa a planilha de Contas a Pagar da Omie (só os marcados, uma conta por colaborador, com o vencimento e a taxa deste boleto)">
                        Anexar planilha Omie
                      </label>
                    </div>
                    {ajustesUnidade.length ? (
                      <div className="small" style={{ maxWidth: 320 }}
                        title="Repasse + taxa de serviço − desconto do fechamento de cada unidade, rateado entre os colaboradores dela (negativo = abatimento). Só no relatório e na planilha Omie — não entra no arquivo da plataforma.">
                        <div className="form-label small fw-semibold mb-1">Ajuste rateado por unidade</div>
                        {ajustesUnidade.map(a => (
                          <div key={a.nome}>{a.nome}: <b style={{ color: a.valor < 0 ? '#a11d2b' : '#116b47' }}>{fmtAjuste(a.valor)}</b></div>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <label className="form-label small fw-semibold">Taxa do boleto</label>
                        <input type="text" className="form-control form-control-sm"
                          style={{ width: '120px' }} placeholder="381,22"
                          value={boletoForm.taxa_boleto}
                          onChange={(e) => { setBoletoForm(f => ({ ...f, taxa_boleto: e.target.value })); setBoletoPrevia(null); }}
                          title="Rateada igualmente entre os colaboradores SÓ no relatório do e-mail — não entra no arquivo da plataforma." />
                      </div>
                    )}
                    <div>
                      <label className="form-label small fw-semibold">Enviar como</label>
                      <select className="form-select form-select-sm" style={{ width: 'auto' }}
                        value={boletoOperador}
                        onChange={(e) => { setBoletoOperador(e.target.value); setBoletoPrevia(null); }}
                        title="O e-mail sai da conta deste operador">
                        <option value="">— escolha o operador —</option>
                        {(boletoDados?.operadores || []).map(o => (
                          <option key={o.id} value={o.id} disabled={!o.pronto}>
                            {o.nome}{o.pronto ? '' : ' (sem senha de app)'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button className="btn btn-sm btn-outline-secondary" onClick={salvarFinanceiro}
                      title="Guardar estes e-mails para os próximos meses">
                      <i className="fas fa-save me-2"></i>Salvar e-mails
                    </button>
                    <button className="btn btn-sm btn-primary ms-auto" onClick={previaBoleto}>
                      <i className="fas fa-eye me-2"></i>Ver o e-mail
                    </button>
                  </div>
                  <div className="form-text">
                    A relação do e-mail leva só as {linhasDoEmail().length} linha(s) marcadas como recarregadas
                    desta competência — uma por colaborador e operadora, com dias e valor.
                  </div>
                  {linhasDoEmail().length === 0 && (
                    <div className="alert alert-warning small py-2 mt-2 mb-0">
                      Nenhuma linha marcada como recarregada nesta competência — marque na tabela quem foi
                      recarregado antes de enviar ao financeiro.
                    </div>
                  )}

                  {/* 3. prévia */}
                  {boletoPrevia && (
                    <>
                      <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                        <i className="fas fa-envelope-open-text me-2"></i>Prévia
                      </h6>
                      <div className="small mb-2">
                        <div><b>Assunto:</b> {boletoPrevia.assunto}</div>
                        <div><b>De:</b> {boletoPrevia.remetente || '—'}</div>
                        <div><b>Para:</b> {boletoPrevia.destinatarios || '—'}</div>
                        {boletoPrevia.copia && <div><b>Cópia:</b> {boletoPrevia.copia}</div>}
                        <div>
                          <b>Anexos:</b>{' '}
                          {boletoPrevia.anexos?.length
                            ? boletoPrevia.anexos.map(a => a.nome).join(', ')
                            : <span className="text-danger">nenhum — anexe o boleto</span>}
                        {boletoPrevia.relatorio && (
                          <span className="ms-1 text-success" title="Planilha com a relação por colaborador, gerada na hora do envio">
                            + <i className="fas fa-file-excel me-1"></i>{boletoPrevia.relatorio}
                          </span>
                        )}
                        {boletoPrevia.omie && (boletoPrevia.omie.problema ? (
                          <div className="text-danger">
                            <i className="fas fa-triangle-exclamation me-1"></i>Planilha Omie: {boletoPrevia.omie.problema}
                          </div>
                        ) : (
                          <span className="ms-1 text-success" title="Contas a Pagar da Omie: uma conta por colaborador marcado">
                            + <i className="fas fa-file-excel me-1"></i>{boletoPrevia.omie.nome}
                          </span>
                        ))}
                        </div>
                      </div>
                      <div className="border rounded" style={{ maxHeight: '340px', overflow: 'auto' }}>
                        {/* sandbox="" é obrigatório: srcdoc herda a origem do app,
                            então script no HTML da prévia alcançaria o token no
                            localStorage. O logo em data: URI segue funcionando. */}
                        <iframe title="Prévia do e-mail do boleto de VT"
                          srcDoc={boletoPrevia.corpo_previa || boletoPrevia.corpo_html}
                          sandbox=""
                          style={{ width: '100%', height: '340px', border: 0 }} />
                      </div>
                    </>
                  )}

                  {/* 4. histórico */}
                  {(boletoDados?.historico || []).length > 0 && (
                    <>
                      <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                        <i className="fas fa-clock-rotate-left me-2"></i>
                        Já enviados nesta competência
                      </h6>
                      {boletoDados.historico.map((h, i) => (
                        <div key={i} className="small text-muted border-bottom py-1">
                          <b style={{ color: '#212529' }}>{h.enviado_em}</b> · {h.destinatarios}
                          {h.colaboradores != null && ` · ${h.colaboradores} linha(s)`}
                          {h.valor_total != null && ` · ${brl(h.valor_total)}`}
                          {h.anexos && ` · ${h.anexos}`}
                          {h.enviado_por && ` · por ${h.enviado_por}`}
                        </div>
                      ))}
                    </>
                  )}
                </div>

                <div className="modal-footer bg-light">
                  <span className="me-auto small text-muted">
                    O e-mail sai com o boleto em anexo e a relação dos colaboradores.
                  </span>
                  <button className="btn btn-secondary" disabled={enviandoBoleto}
                    onClick={() => { setBoletoAberto(false); setBoletoPrevia(null); }}>
                    <i className="fas fa-times me-2"></i>Fechar
                  </button>
                  <button className="btn btn-success" onClick={enviarBoleto}
                    disabled={enviandoBoleto || !boletoPrevia || !boletoSel.length
                      || !boletoForm.emails.trim() || !boletoOperador}
                    title={!boletoPrevia ? 'Clique em "Ver o e-mail" antes de enviar' : undefined}>
                    <i className={`fas ${enviandoBoleto ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {enviandoBoleto ? 'Enviando...' : 'Enviar ao financeiro'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {planilhaAberto && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
              overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header"
                  style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-file-excel me-2"></i>
                      Planilha de controle do vale transporte
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {dados?.cliente} · {mesExtenso(dados?.mes_uso || mesDepois(mes))} · {linhasPlanilha().length} colaborador(es)
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => { setPlanilhaAberto(false); setPlanilhaPrevia(null); }}
                    disabled={enviandoPlanilha}></button>
                </div>

                <div className="modal-body">
                  {/* a planilha que vai anexada — mesmo layout da aba Controle do cliente */}
                  <div className="border rounded p-3 mb-3" style={{ background: '#fff' }}>
                    <div className="d-flex align-items-start mb-2">
                      <div>
                        <div className="fw-bold" style={{ fontSize: '1.05rem' }}>
                          Planilha de controle Vale Transporte
                        </div>
                        <div className="fw-semibold text-muted">
                          {mesPorExtenso(dados?.mes_uso || mesDepois(mes))}
                        </div>
                      </div>
                      <span className="ms-auto badge bg-success-subtle text-success border border-success-subtle"
                        title="Gerada na hora do envio — não precisa anexar nada">
                        <i className="fas fa-paperclip me-1"></i>vai anexada ao e-mail
                      </span>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0" style={{ fontSize: '.8rem' }}>
                        <thead>
                          <tr style={{ whiteSpace: 'nowrap' }}>
                            {['Funcionario', 'CPF Funcionario', 'Departamento', 'Centro de custo', 'Dias',
                              'Valor Unitário', 'Valor por dia', 'Custo Empresa'].map(h => (
                              <th key={h} className="text-center"
                                style={{ background: '#00205b', color: '#fff', fontWeight: 600 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {linhasPlanilha().map((l, i) => (
                            <tr key={i}>
                              <td>{l.nome}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtCpf(l.cpf)}</td>
                              <td>{l.departamento}</td>
                              <td>{l.centro_custo}</td>
                              <td className="text-center">{l.dias}</td>
                              <td className="text-end">{l.valor_unitario != null ? brl(l.valor_unitario) : '—'}</td>
                              <td className="text-end">{brl(l.valor_dia)}</td>
                              <td className="text-end">{brl(l.custo)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={7} className="text-end fw-bold">Total</td>
                            <td className="text-end fw-bold" style={{ color: '#116b47' }}>
                              {brl(linhasPlanilha().reduce((n, l) => n + (l.custo || 0), 0))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Para</label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="rh@cliente.com.br"
                        value={boletoForm.emails}
                        onChange={(e) => { setBoletoForm(f => ({ ...f, emails: e.target.value })); setPlanilhaPrevia(null); }} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Cópia (opcional)</label>
                      <input type="text" className="form-control form-control-sm"
                        value={boletoForm.copia}
                        onChange={(e) => { setBoletoForm(f => ({ ...f, copia: e.target.value })); setPlanilhaPrevia(null); }} />
                    </div>
                  </div>
                  <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
                    <div>
                      <label className="form-label small fw-semibold">Enviar como</label>
                      <select className="form-select form-select-sm" style={{ width: 'auto' }}
                        value={boletoOperador}
                        onChange={(e) => { setBoletoOperador(e.target.value); setPlanilhaPrevia(null); }}
                        title="O e-mail sai da conta deste operador">
                        <option value="">— escolha o operador —</option>
                        {(boletoDados?.operadores || []).map(o => (
                          <option key={o.id} value={o.id} disabled={!o.pronto}>
                            {o.nome}{o.pronto ? '' : ' (sem senha de app)'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button className="btn btn-sm btn-outline-secondary" onClick={salvarFinanceiro}
                      title="Guardar estes e-mails para os próximos meses">
                      <i className="fas fa-save me-2"></i>Salvar e-mails
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={baixarPlanilha}
                      disabled={!linhasPlanilha().length}
                      title="Baixar a planilha para conferir antes de enviar">
                      <i className="fas fa-download me-2"></i>Baixar cópia
                    </button>
                    <button className="btn btn-sm btn-primary ms-auto" onClick={previaPlanilha}
                      disabled={!linhasPlanilha().length}>
                      <i className="fas fa-eye me-2"></i>Ver o e-mail
                    </button>
                  </div>
                  <div className="form-check form-switch mt-3">
                    <input className="form-check-input" type="checkbox" id="vtEnvioPlanilha"
                      checked={dados?.envio_vt === 'planilha'}
                      onChange={(e) => trocarEnvioVt(e.target.checked ? 'planilha' : 'boleto')} />
                    <label className="form-check-label small" htmlFor="vtEnvioPlanilha">
                      Este cliente não recebe boleto — a planilha fica no lugar do botão "Boleto e e-mail"
                    </label>
                  </div>
                  {!linhasPlanilha().length && (
                    <div className="alert alert-warning small py-2 mt-3 mb-0">
                      Nenhum colaborador com valor nesta competência — cadastre as linhas de VT
                      em <b>Configurar</b> antes de enviar.
                    </div>
                  )}

                  {planilhaPrevia && (
                    <>
                      <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                        <i className="fas fa-envelope-open-text me-2"></i>Prévia
                      </h6>
                      <div className="small mb-2">
                        <div><b>Assunto:</b> {planilhaPrevia.assunto}</div>
                        <div><b>De:</b> {planilhaPrevia.remetente || '—'}</div>
                        <div><b>Para:</b> {planilhaPrevia.destinatarios || '—'}</div>
                        {planilhaPrevia.copia && <div><b>Cópia:</b> {planilhaPrevia.copia}</div>}
                        <div><b>Anexo:</b> <i className="fas fa-file-excel text-success me-1"></i>{planilhaPrevia.anexo}</div>
                      </div>
                      <div className="border rounded" style={{ maxHeight: '340px', overflow: 'auto' }}>
                        <iframe title="Prévia do e-mail da planilha de VT"
                          srcDoc={planilhaPrevia.corpo_previa || planilhaPrevia.corpo_html}
                          sandbox=""
                          style={{ width: '100%', height: '340px', border: 0 }} />
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer bg-light">
                  <span className="me-auto small text-muted">
                    O e-mail sai com a planilha de controle em anexo.
                  </span>
                  <button className="btn btn-secondary" disabled={enviandoPlanilha}
                    onClick={() => { setPlanilhaAberto(false); setPlanilhaPrevia(null); }}>
                    <i className="fas fa-times me-2"></i>Fechar
                  </button>
                  <button className="btn btn-success" onClick={enviarPlanilha}
                    disabled={enviandoPlanilha || !planilhaPrevia
                      || !boletoForm.emails.trim() || !boletoOperador}
                    title={!planilhaPrevia ? 'Clique em "Ver o e-mail" antes de enviar' : undefined}>
                    <i className={`fas ${enviandoPlanilha ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {enviandoPlanilha ? 'Enviando...' : 'Enviar planilha'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {omieVt && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-file-export me-2"></i>Planilha Omie — vale transporte
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {dados?.cliente} · recarga de {mesExtenso(dados?.mes_uso || mesDepois(mes))} · só os colaboradores marcados como recarregados
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setOmieVt(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Vencimento do boleto</label>
                      <input type="text" className={`form-control form-control-sm ${(omieVt.previa?.linhas || []).some(l => l.pendencias.includes('vencimento')) ? 'border-warning' : ''}`}
                        placeholder="05/11/2026"
                        value={omieVt.vencimento} onChange={(e) => mudarOmieVt('vencimento', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      {omieVt.previa?.por_unidade ? (
                        <div className="small" title="Repasse + taxa de serviço − desconto do fechamento de cada unidade, dividido igualmente entre os colaboradores marcados dela (negativo = abatimento)">
                          <div className="form-label small fw-semibold mb-1">Ajuste rateado por unidade</div>
                          {(omieVt.previa.ajustes || []).map(a => (
                            <div key={a.unidade}>{a.unidade}: <b style={{ color: a.valor < 0 ? '#a11d2b' : '#116b47' }}>{fmtAjuste(a.valor)}</b></div>
                          ))}
                        </div>
                      ) : (<>
                        <label className="form-label small fw-semibold">Taxa do boleto (dividida entre todos)</label>
                        <input type="text" className="form-control form-control-sm" value={omieVt.taxa}
                          onChange={(e) => mudarOmieVt('taxa', e.target.value)}
                          title="Dividida em partes iguais entre os colaboradores marcados" />
                      </>)}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Categoria do VT na Omie</label>
                      <input type="text" className="form-control form-control-sm" value={omieVt.categoria}
                        onChange={(e) => mudarOmieVt('categoria', e.target.value)}
                        title="Vale para as unidades de vale transporte. Unidade com 'Mobilidade' no nome sai sempre na categoria Mobilidade." />
                      <div className="form-text">Mobilidade sai na categoria Mobilidade.</div>
                    </div>
                    <div className="col-md-3 text-end">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => previaOmieVt(omieVt)} disabled={omieVt.carregando}>
                        <i className={`fas ${omieVt.carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>Atualizar prévia
                      </button>
                    </div>
                  </div>

                  {omieVt.previa && (
                    <>
                      <div className="table-responsive mt-3">
                        <table className="table table-sm align-middle" style={{ fontSize: '.82rem' }}>
                          <thead className="table-light">
                            <tr><th>Fornecedor (colaborador)</th><th>Unidade</th><th>Projeto</th><th>Departamento</th>
                              <th className="text-end">VT</th>
                              <th className="text-end">Taxa</th><th className="text-end">Valor da conta</th></tr>
                          </thead>
                          <tbody>
                            {(omieVt.previa.linhas || []).map((l) => (
                              <tr key={`${l.operadora_id}-${l.cpf || l.nome}`}>
                                <td className="fw-semibold">{l.nome}</td>
                                <td>{l.unidade}</td>
                                <td>{l.projeto || <span className="text-warning-emphasis small">sem projeto</span>}</td>
                                <td>
                                  {l.pendencias.includes('departamento') ? (
                                    <select className="form-select form-select-sm border-warning" value={omieVt.departamentos[l.chave_depto] || ''}
                                      onChange={(e) => mudarOmieVt('departamentos', { ...omieVt.departamentos, [l.chave_depto]: e.target.value })}
                                      title={`Na Convenia: ${l.depto_convenia || 'vazio'}`}>
                                      <option value="">Escolha… ({l.depto_convenia || 'sem depto'})</option>
                                      {(omieVt.previa.departamentos || []).map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                  ) : l.departamento}
                                </td>
                                <td className="text-end">{brl(l.valor_vt)}</td>
                                <td className="text-end">{brl(l.valor_taxa)}</td>
                                <td className="text-end fw-bold">{brl(l.valor)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="fw-bold"><td colSpan={4} className="text-end">Total ({omieVt.previa.marcados} colaborador(es))</td>
                              <td></td><td className="text-end">{brl(omieVt.previa.taxa)}</td>
                              <td className="text-end" style={{ color: '#116b47' }}>{brl(omieVt.previa.total)}</td></tr>
                          </tfoot>
                        </table>
                      </div>
                      {(omieVt.previa.avisos || []).map((a, i) => (
                        <div key={i} className="small text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>{a}</div>
                      ))}
                      <div className="form-text">Departamento escolhido aqui fica salvo para cada colaborador e vale também na Importação Omie dos salários. Depois de mudar algo, clique em "Atualizar prévia".</div>
                    </>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <span className="me-auto small text-muted">
                    {(() => {
                      const ls = omieVt.previa?.linhas || [];
                      const falta = [...new Set(ls.flatMap(l => l.pendencias))];
                      if (falta.length) {
                        return <span className="text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>
                          Para baixar, resolva: {falta.map(f => ({ vencimento: 'vencimento do boleto', departamento: 'departamento' }[f] || f)).join(' e ')} e clique em "Atualizar prévia".</span>;
                      }
                      return 'Uma conta por colaborador marcado: VT recarregado + a parte dele da taxa.';
                    })()}
                  </span>
                  <button className="btn btn-secondary" onClick={() => setOmieVt(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                  <button className="btn btn-success" onClick={baixarOmieVt}
                    disabled={omieVt.carregando || !omieVt.previa || !(omieVt.previa.linhas || []).length
                      || (omieVt.previa.linhas || []).some(l => l.pendencias.length)}
                    title="Resolva o que está em amarelo e atualize a prévia antes de baixar">
                    <i className="fas fa-download me-2"></i>Baixar planilha
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL — excluir operadora */}
        {operadoraExcluir && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header"
                  style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-trash me-2"></i>Excluir {operadoraExcluir.nome}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {operadoraExcluir.qtd} colaborador(es) cadastrado(s) nela
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => setOperadoraExcluir(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    O cadastro de todos eles nesta operadora é apagado. Se já houver
                    recarga registrada, a exclusão é recusada para não deixar o
                    histórico órfão.
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setOperadoraExcluir(null)}>
                    Cancelar
                  </button>
                  <button className="btn btn-danger"
                    onClick={() => excluirOperadora(operadoraExcluir)}>
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BpoRhValeTransporte;
